import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { ReactComponent as LockIcon } from '../../svgs/lock.svg';
import { ReactComponent as ShieldIcon } from '../../svgs/shield.svg';
import { ReactComponent as DeleteIcon } from '../../svgs/delete.svg';
import { HistoryIcon } from '../../components/icons/SettingsIcons';
import BaseControl from '../../components/BaseControl';
import SwitchControl from '../../components/SwitchControl';
import CStack from '../../components/CStack';
import { useAuth } from '../../api/auth/store';
import usePasswordConfirmation from '../../hooks/usePasswordConfirmation';
import useUXStore from '../../api/ux/store';

interface AccountSectionProps {
  onChangePassword: () => void;
  onViewSecurityActivity: () => void;
  onDeleteAccount: () => void;
  isOwner?: boolean;
}

// Master two-factor toggle. When on, a password login must be followed by a second factor
// (passkey or MPIN). It can only be enabled once at least one factor is configured.
const TwoFactorToggle: React.FC = () => {
  const { mfaSettings, fetchMfaSettings, updateMfaToggles } = useAuth();
  const { confirm: confirmPassword } = usePasswordConfirmation();
  const { addToast } = useUXStore();

  useEffect(() => {
    fetchMfaSettings();
  }, [fetchMfaSettings]);

  const hasFactor = !!(mfaSettings?.hasPinSetup || mfaSettings?.hasPasskeySetup);
  const enabled = !!mfaSettings?.isMfaEnabled;

  const handleToggle = (val: boolean) => {
    if (val && !hasFactor) {
      addToast("", "Set up an MPIN or passkey first to enable two-factor authentication.");
      return;
    }
    confirmPassword(async (password: string) => {
      const res = await updateMfaToggles({ enable_mfa: val, password });
      if (res.Code === 200) {
        addToast("", `Two-factor authentication ${val ? "enabled" : "disabled"}.`);
      } else {
        addToast("Error", (typeof res.Msg === "string" && res.Msg) || "Failed to update two-factor authentication.");
      }
    }, {
      title: "Security Verification",
      message: `Enter your password to ${val ? "enable" : "disable"} two-factor authentication.`
    });
  };

  return (
    <SwitchControl
      text="Two-Factor Authentication"
      caption="Require a second factor (passkey or MPIN) when signing in"
      icon={ShieldIcon}
      value={enabled}
      edit={true}
      onChanged={handleToggle}
    />
  );
};

const AccountSection: React.FC<AccountSectionProps> = ({
  onChangePassword,
  onViewSecurityActivity,
  onDeleteAccount,
  isOwner = false
}) => {

  const securityOptions = [
    {
      id: 'change-password',
      title: 'Change Password',
      description: 'Update your account password',
      icon: LockIcon,
      action: onChangePassword,
      enabled: true,
    },
    {
      id: 'security-logs',
      title: 'Security Activity',
      description: 'View recent security events',
      icon: HistoryIcon,
      action: onViewSecurityActivity,
      enabled: true,
    },
    {
      id: 'delete-account',
      title: 'Delete Account',
      description: 'Permanently remove your account and data',
      icon: DeleteIcon,
      action: onDeleteAccount,
      enabled: true,
      hidden: isOwner,
      textColor: 'red'
    },
  ];

  return (
    <>
      <CStack header="Account Security">
        {securityOptions
          .filter(option => !option.hidden)
          .map((option) => (
            <React.Fragment key={option.id}>
              <BaseControl
                text={option.title}
                caption={option.description}
                icon={option.icon}
                enabled={option.enabled}
                onClick={option.action}
                contentSize={2}
              >
                {option.enabled && (
                  <span className="text-muted">›</span>
                )}
              </BaseControl>
              {/* Place the functional 2FA toggle right after Change Password */}
              {option.id === 'change-password' && <TwoFactorToggle />}
            </React.Fragment>
          ))}
      </CStack>

      <Card className="mt-4">
        <Card.Body>
          <h5 className="mb-3">Security Recommendations</h5>
          <ul className="mb-0">
            <li>Use a strong, unique password for your account</li>
            <li>Enable two-factor authentication when available</li>
            <li>Regularly review your security activity</li>
            <li>Never share your password or OTP codes with anyone</li>
          </ul>
        </Card.Body>
      </Card>
    </>
  );
};

export default AccountSection;